import { useCallback, useRef, useState } from 'react';
import { geographyService } from '@/services/geography';

const PIN_CODE_REGEX = /^\d{6}$/;

export type PincodeLookupOutcome =
  // M-04 adds `stateId` / `cityId` beside the names. A caller that only needs
  // to fill a text/name-valued box keeps reading the names exactly as before;
  // a caller that has to SCOPE another lookup by the resolved geography (the
  // Add Policy RTO block: cities by state, RTOs by city) needs the ids, and
  // re-deriving them by matching the name back against the master is how
  // legacy spellings quietly resolve to nothing. Both are optional: a payload
  // cached before `state` was added to PincodeLookupSerializer carries the
  // names only, so a consumer must degrade to name matching rather than break.
  | { status: 'found'; stateName: string; stateId?: string; cityName?: string; cityId?: string }
  | { status: 'not_found' }
  | { status: 'error' };

/**
 * B6 -- the one place that knows how to turn a 6-digit PIN code into a
 * State (and City, where the match has one) via the only pincode-by-code
 * lookup the codebase exposes: GET /geography/pincodes/search/?code=
 * (PincodeViewSet.search, backend/apps/geography/views.py).
 *
 * `lookup` never throws -- a network failure and "no match for this code"
 * both come back as a typed outcome rather than a rejected promise, so a
 * caller can always show a quiet "couldn't auto-fill, choose manually" hint
 * instead of an error boundary or a blocked form.
 *
 * Results are cached per code for the life of the hook instance. That is
 * what lets a caller re-run `lookup` on every blur of the same input without
 * re-querying the server, and — more importantly — without re-firing the
 * side effect that fills State/City every time the field merely loses focus
 * again. A caller should still gate on "the code actually changed since the
 * last successful fill" itself (see AddressFields) so a user's manual
 * override of an auto-filled State is never clobbered by a stray re-blur.
 */
export function usePincodeLookup() {
  const [loading, setLoading] = useState(false);
  const cache = useRef(new Map<string, PincodeLookupOutcome>());

  const lookup = useCallback(async (rawCode: string): Promise<PincodeLookupOutcome> => {
    const code = rawCode.trim();
    if (!PIN_CODE_REGEX.test(code)) {
      return { status: 'not_found' };
    }
    const cached = cache.current.get(code);
    if (cached) {
      return cached;
    }
    setLoading(true);
    try {
      const { results } = await geographyService.searchPincodes(code);
      const exact = results.find((r) => r.code === code) || results[0];
      const outcome: PincodeLookupOutcome =
        exact && exact.state_name
          ? {
              status: 'found',
              stateName: exact.state_name,
              stateId: exact.state || undefined,
              cityName: exact.city_name || undefined,
              cityId: exact.city || undefined,
            }
          : { status: 'not_found' };
      cache.current.set(code, outcome);
      return outcome;
    } catch {
      // Deliberately not cached in THIS hook: a transient network failure
      // should not permanently poison this code inside `usePincodeLookup`
      // itself. Note this does not, on its own, make a failed code retry on
      // the next blur -- every caller (AddressFields, the wizard steps,
      // PublicQuoteJourney) records the code as "already handled" in its
      // OWN ref before awaiting `lookup()`, specifically so a repeat blur of
      // the same unchanged code never re-fires the request. A caller that
      // wants a failed code retried on a later blur would need to skip that
      // bookkeeping on an 'error' outcome -- none currently does, since a
      // silent retry-on-blur wasn't asked for by the spec.
      return { status: 'error' };
    } finally {
      setLoading(false);
    }
  }, []);

  return { lookup, loading };
}
